import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CarFront } from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { YAKIT, VITES } from "../lib/constants";
import { today } from "../lib/format";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function AddVehicle() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    marka: "",
    model: "",
    yil: new Date().getFullYear(),
    plaka: "",
    km: 0,
    renk: "",
    yakit: "Benzin",
    vites: "Manuel",
    alisFiyati: 0,
    alisTarihi: today(),
    notlar: "",
    gorsel: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/vehicles", {
        ...form,
        yil: Number(form.yil),
        km: Number(form.km),
        alisFiyati: Number(form.alisFiyati),
      });
      toast.success("Araç stoğa eklendi");
      nav("/stok");
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error(apiErrorMessage(err));
        nav("/abonelik");
      } else {
        toast.error(apiErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full h-11 px-3.5 rounded-xl border border-stone-300 bg-white focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20";

  return (
    <div className="max-w-4xl" data-testid="add-vehicle-page">
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
          Araç Ekle
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
          Yeni araç stoğa alın
        </h1>
        <p className="text-stone-500 mt-2 max-w-2xl">
          Aracın temel bilgilerini girin. Sonraki adımda masraf ekleyebilir,
          satış işleminden sonra da net kâr otomatik hesaplanır.
        </p>
      </div>

      <form
        onSubmit={submit}
        className="rounded-2xl border border-stone-200 bg-white p-6 md:p-8 space-y-5"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Marka">
            <input
              required
              data-testid="veh-marka"
              value={form.marka}
              onChange={(e) => set("marka", e.target.value)}
              placeholder="Renault"
              className={inputCls}
            />
          </Field>
          <Field label="Model">
            <input
              required
              data-testid="veh-model"
              value={form.model}
              onChange={(e) => set("model", e.target.value)}
              placeholder="Clio Touch"
              className={inputCls}
            />
          </Field>
          <Field label="Yıl">
            <input
              type="number"
              required
              min="1980"
              max="2100"
              data-testid="veh-yil"
              value={form.yil}
              onChange={(e) => set("yil", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Plaka">
            <input
              required
              data-testid="veh-plaka"
              value={form.plaka}
              onChange={(e) => set("plaka", e.target.value.toUpperCase())}
              placeholder="34 ABC 123"
              className={inputCls}
            />
          </Field>
          <Field label="Kilometre">
            <input
              type="number"
              data-testid="veh-km"
              value={form.km}
              onChange={(e) => set("km", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Renk">
            <input
              data-testid="veh-renk"
              value={form.renk}
              onChange={(e) => set("renk", e.target.value)}
              placeholder="Beyaz"
              className={inputCls}
            />
          </Field>
          <Field label="Yakıt">
            <select
              data-testid="veh-yakit"
              value={form.yakit}
              onChange={(e) => set("yakit", e.target.value)}
              className={inputCls}
            >
              {YAKIT.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </Field>
          <Field label="Vites">
            <select
              data-testid="veh-vites"
              value={form.vites}
              onChange={(e) => set("vites", e.target.value)}
              className={inputCls}
            >
              {VITES.map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </Field>
          <Field label="Alış Tarihi">
            <input
              type="date"
              required
              data-testid="veh-alis-tarihi"
              value={form.alisTarihi}
              onChange={(e) => set("alisTarihi", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Alış Fiyatı (₺)">
            <input
              type="number"
              required
              min="0"
              data-testid="veh-alis-fiyati"
              value={form.alisFiyati}
              onChange={(e) => set("alisFiyati", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Görsel URL (opsiyonel)">
            <input
              data-testid="veh-gorsel"
              value={form.gorsel}
              onChange={(e) => set("gorsel", e.target.value)}
              placeholder="https://…"
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Notlar">
          <textarea
            data-testid="veh-notlar"
            value={form.notlar}
            onChange={(e) => set("notlar", e.target.value)}
            rows={3}
            className="w-full px-3.5 py-2.5 rounded-xl border border-stone-300 bg-white focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20"
            placeholder="Boyasız, hasarsız, tam servis bakımlı…"
          />
        </Field>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-stone-100">
          <button
            type="button"
            onClick={() => nav("/stok")}
            className="h-11 px-5 rounded-full border border-stone-300 text-slate-800 hover:bg-stone-50"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            data-testid="veh-submit"
            disabled={busy}
            className="h-11 px-6 rounded-full bg-orange-700 hover:bg-orange-800 text-white font-semibold flex items-center gap-2 disabled:opacity-60"
          >
            <CarFront className="w-4 h-4" />
            {busy ? "Kaydediliyor…" : "Stoğa Ekle"}
          </button>
        </div>
      </form>
    </div>
  );
}
